import React, { useState, useMemo } from 'react';
import { ticketsAPI, projectsAPI } from '../../api/api';
import TaskList from './TaskList';
import {
  DocumentChartBarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';

const AllProjectsTasks = ({ user, projects = [] }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [allProjectsData, setAllProjectsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState('all'); // 'all', 'today', 'week', 'month', '3months', '6months', 'year'
  const [allRawTickets, setAllRawTickets] = useState(null); // Filtrelenmemiş tüm ticket'lar

  // Helper fonksiyonlar
  const getStatusName = (ticket) => {
    if (!ticket) return null;
    if (typeof ticket.status === 'object' && ticket.status?.status_name) {
      return ticket.status.status_name;
    }
    if (typeof ticket.status === 'string') {
      return ticket.status;
    }
    return null;
  };

  const getPriorityName = (ticket) => {
    if (!ticket) return 'Medium';
    if (typeof ticket.priority === 'object' && ticket.priority?.priority_name) {
      return ticket.priority.priority_name;
    }
    if (typeof ticket.priority === 'string') {
      return ticket.priority;
    }
    return 'Medium';
  };

  // Tarih filtresine göre ticket'ları filtrele
  const filterTicketsByDate = (tickets, filterType) => {
    if (!tickets || tickets.length === 0) return [];
    if (filterType === 'all') return tickets;

    const now = new Date();
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);

    return tickets.filter(ticket => {
      // Tamamlanan ticket'lar için updated_at, açık ticket'lar için created_at kullan
      const ticketDate = new Date(ticket.updated_at || ticket.created_at || 0);
      ticketDate.setHours(0, 0, 0, 0);

      switch (filterType) {
        case 'today':
          return ticketDate.getTime() === today.getTime();
        case 'week':
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 7);
          return ticketDate >= weekAgo;
        case 'month':
          const monthAgo = new Date(today);
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          return ticketDate >= monthAgo;
        case '3months':
          const threeMonthsAgo = new Date(today);
          threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
          return ticketDate >= threeMonthsAgo;
        case '6months':
          const sixMonthsAgo = new Date(today);
          sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
          return ticketDate >= sixMonthsAgo;
        case 'year':
          const yearAgo = new Date(today);
          yearAgo.setFullYear(yearAgo.getFullYear() - 1);
          return ticketDate >= yearAgo;
        default:
          return true;
      }
    });
  };

  // Tarih filtresine göre verileri güncelle
  const applyDateFilter = useMemo(() => {
    if (!allRawTickets || allRawTickets.length === 0) {
      return allProjectsData;
    }

    const filteredTickets = filterTicketsByDate(allRawTickets, dateFilter);

    // Ticket'ları kategorilere ayır
    const openTickets = filteredTickets.filter(t => {
      const status = getStatusName(t);
      return status === 'Beklemede' || status === 'Yapılıyor' || status === 'To Do' || status === 'In Progress';
    });

    const completedTickets = filteredTickets.filter(t => {
      const status = getStatusName(t);
      return status === 'Bitti' || status === 'Done';
    });

    const pendingTickets = filteredTickets.filter(t => {
      const status = getStatusName(t);
      return status === 'Beklemede' || status === 'To Do';
    });

    const urgentTickets = filteredTickets.filter(t => {
      const priority = getPriorityName(t);
      return priority === 'High' || priority === 'Urgent' || priority === 'Yüksek' || priority === 'Acil';
    });

    // Son tamamlanan görevler
    const recentCompleted = completedTickets
      .sort((a, b) => {
        const dateA = new Date(a.updated_at || a.created_at || 0);
        const dateB = new Date(b.updated_at || b.created_at || 0);
        return dateB - dateA;
      })
      .map(ticket => ({
        id: ticket.id || 'N/A',
        title: ticket.title || 'Başlıksız Ticket',
        status: getStatusName(ticket) || 'Bitti',
        priority: getPriorityName(ticket),
        project_name: ticket.project_name,
        project_code: ticket.project_code,
        updated_at: ticket.updated_at,
        created_at: ticket.created_at
      }))
      .filter(task => task.title && task.id && task.id !== 'N/A');

    // Bugün tamamlanan görevler
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayCompleted = completedTickets.filter(ticket => {
      const updatedDate = new Date(ticket.updated_at || ticket.created_at || 0);
      updatedDate.setHours(0, 0, 0, 0);
      return updatedDate.getTime() === today.getTime();
    }).length;

    // Açık görevler listesi
    const openTasksList = openTickets
      .sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA;
      })
      .map(ticket => ({
        id: ticket.id || 'N/A',
        title: ticket.title || 'Başlıksız Ticket',
        status: getStatusName(ticket) || 'Bilinmiyor',
        priority: getPriorityName(ticket),
        project_name: ticket.project_name,
        project_code: ticket.project_code,
        updated_at: ticket.updated_at,
        created_at: ticket.created_at
      }))
      .filter(task => task.title && task.id && task.id !== 'N/A');

    return {
      totalTickets: filteredTickets.length,
      openTickets: openTickets.length,
      completedTickets: completedTickets.length,
      pendingTickets: pendingTickets.length,
      urgentTickets: urgentTickets.length,
      todayCompleted: todayCompleted,
      openTasksList: openTasksList,
      completedTasksList: recentCompleted
    };
  }, [allRawTickets, allProjectsData, dateFilter]);

  // Tüm projelerden kullanıcıya atanan görevleri yükle
  const loadAllProjectsTasks = async () => {
    if (allProjectsData && isExpanded) {
      // Zaten yüklenmiş ve açıksa sadece kapat
      setIsExpanded(false);
      return;
    }

    if (!allProjectsData) {
      // İlk kez açılıyorsa verileri yükle
      try {
        setLoading(true);

        // Tüm projeleri getir
        const projectsResponse = await projectsAPI.getProjects();
        const allProjects = projectsResponse?.data || projects || [];

        // Tüm projelerden ticket'ları topla
        const allMyTickets = [];

        for (const project of allProjects) {
          try {
            const ticketsResponse = await ticketsAPI.getTicketsByProject(project.id);
            if (ticketsResponse?.success && ticketsResponse?.tickets) {
              // Tickets grouped by status - convert to flat array
              let ticketsArray = [];
              if (typeof ticketsResponse.tickets === 'object') {
                Object.keys(ticketsResponse.tickets).forEach(status => {
                  if (Array.isArray(ticketsResponse.tickets[status])) {
                    ticketsResponse.tickets[status].forEach(ticket => {
                      ticketsArray.push({
                        ...ticket,
                        project_name: project.project_name,
                        project_code: project.project_code,
                        status: status
                      });
                    });
                  }
                });
              } else if (Array.isArray(ticketsResponse.tickets)) {
                ticketsArray = ticketsResponse.tickets.map(ticket => ({
                  ...ticket,
                  project_name: project.project_name,
                  project_code: project.project_code
                }));
              }

              // Sadece kullanıcıya atanan ticket'ları filtrele
              const myTickets = ticketsArray.filter(ticket => {
                if (!user?.id) return false;
                // assigned_to_user object ise id'yi al
                if (ticket.assigned_to_user && typeof ticket.assigned_to_user === 'object') {
                  return ticket.assigned_to_user.id === user.id;
                }
                // assigned_to_user_id direkt varsa onu kullan
                if (ticket.assigned_to_user_id) {
                  return ticket.assigned_to_user_id === user.id;
                }
                return false;
              });

              allMyTickets.push(...myTickets);
            }
          } catch (error) {
            // Silently handle error
          }
        }

        // Ticket'ları kategorilere ayır
        const openTickets = allMyTickets.filter(t => {
          const status = getStatusName(t);
          return status === 'Beklemede' || status === 'Yapılıyor' || status === 'To Do' || status === 'In Progress';
        });

        const completedTickets = allMyTickets.filter(t => {
          const status = getStatusName(t);
          return status === 'Bitti' || status === 'Done';
        });

        const pendingTickets = allMyTickets.filter(t => {
          const status = getStatusName(t);
          return status === 'Beklemede' || status === 'To Do';
        });

        const urgentTickets = allMyTickets.filter(t => {
          const priority = getPriorityName(t);
          return priority === 'High' || priority === 'Urgent' || priority === 'Yüksek' || priority === 'Acil';
        });

        // Son tamamlanan görevler (tümü, en yeni önce sıralı)
        const recentCompleted = completedTickets
          .sort((a, b) => {
            const dateA = new Date(a.updated_at || a.created_at || 0);
            const dateB = new Date(b.updated_at || b.created_at || 0);
            return dateB - dateA;
          })
          .map(ticket => ({
            id: ticket.id || 'N/A',
            title: ticket.title || 'Başlıksız Ticket',
            status: getStatusName(ticket) || 'Bitti',
            priority: getPriorityName(ticket),
            project_name: ticket.project_name,
            project_code: ticket.project_code
          }))
          .filter(task => task.title && task.id && task.id !== 'N/A');

        // Bugün tamamlanan görevler
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayCompleted = completedTickets.filter(ticket => {
          const updatedDate = new Date(ticket.updated_at || ticket.created_at || 0);
          updatedDate.setHours(0, 0, 0, 0);
          return updatedDate.getTime() === today.getTime();
        }).length;

        // Açık görevler listesi (tümü, en yeni önce sıralı)
        const openTasksList = openTickets
          .sort((a, b) => {
            const dateA = new Date(a.created_at || 0);
            const dateB = new Date(b.created_at || 0);
            return dateB - dateA;
          })
          .map(ticket => ({
            id: ticket.id || 'N/A',
            title: ticket.title || 'Başlıksız Ticket',
            status: getStatusName(ticket) || 'Bilinmiyor',
            priority: getPriorityName(ticket),
            project_name: ticket.project_name,
            project_code: ticket.project_code
          }))
          .filter(task => task.title && task.id && task.id !== 'N/A');

        // Filtrelenmemiş tüm ticket'ları sakla (tarih filtresi için)
        setAllRawTickets(allMyTickets);

        // İlk veri yüklemesi - tarih filtresi uygulanmamış
        setAllProjectsData({
          totalTickets: allMyTickets.length,
          openTickets: openTickets.length,
          completedTickets: completedTickets.length,
          pendingTickets: pendingTickets.length,
          urgentTickets: urgentTickets.length,
          todayCompleted: todayCompleted,
          openTasksList: openTasksList,
          completedTasksList: recentCompleted
        });
      } catch (error) {
        // Silently handle error
        alert('Görevler yüklenirken hata oluştu: ' + error.message);
      } finally {
        setLoading(false);
      }
    }

    // Açılır/kapanır durumunu değiştir
    setIsExpanded(!isExpanded);
  };

  // Tarih filtresi label'ı
  const getDateFilterLabel = (filter) => {
    switch (filter) {
      case 'today':
        return 'Bugün';
      case 'week':
        return 'Son 7 Gün';
      case 'month':
        return 'Son 1 Ay';
      case '3months':
        return 'Son 3 Ay';
      case '6months':
        return 'Son 6 Ay';
      case 'year':
        return 'Son 1 Yıl';
      default:
        return '';
    }
  };

  const displayData = applyDateFilter || allProjectsData;

  return (
    <div className="bg-white dark:bg-dark-background-main rounded-xl shadow-sm border border-background-secondary dark:border-dark-border overflow-hidden">
      {/* Header - Buton */}
      <button
        onClick={loadAllProjectsTasks}
        disabled={loading}
        className="w-full flex items-center justify-between p-4 hover:bg-background-main dark:hover:bg-dark-background-secondary transition-colors duration-200"
      >
        <div className="flex items-center gap-3">
          <DocumentChartBarIcon className="w-5 h-5 text-highlight-600 dark:text-highlight-500" />
          <span className="text-lg font-semibold text-text-primary dark:text-dark-text-primary">
            Tüm Projelerden Bana Atanan Görevler
          </span>
          {loading && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-highlight-600"></div>
          )}
        </div>
        {isExpanded ? (
          <ChevronUpIcon className="w-5 h-5 text-text-secondary dark:text-dark-text-secondary" />
        ) : (
          <ChevronDownIcon className="w-5 h-5 text-text-secondary dark:text-dark-text-secondary" />
        )}
      </button>

      {/* Content - Açılır Kapanır */}
      {isExpanded && displayData && (
        <div className="border-t border-background-secondary dark:border-dark-border p-6 space-y-6 animate-in fade-in duration-200">
          {/* Tarih Filtresi */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <FunnelIcon className="w-5 h-5 text-text-secondary dark:text-dark-text-secondary" />
              <span className="text-sm font-medium text-text-primary dark:text-dark-text-secondary">Tarih Filtresi:</span>
            </div>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-4 py-2 border border-background-secondary dark:border-dark-border rounded-lg bg-white dark:bg-dark-background-secondary text-text-primary dark:text-dark-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-highlight-500 focus:border-transparent"
            >
              <option value="all">Tümü</option>
              <option value="today">Bugün</option>
              <option value="week">Son 7 Gün</option>
              <option value="month">Son 1 Ay</option>
              <option value="3months">Son 3 Ay</option>
              <option value="6months">Son 6 Ay</option>
              <option value="year">Son 1 Yıl</option>
            </select>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-highlight-50 dark:bg-highlight-500/10 p-4 rounded-lg">
              <p className="text-sm text-text-secondary dark:text-dark-text-secondary">Toplam Görev</p>
              <p className="text-2xl font-bold text-highlight-600 dark:text-highlight-500">{displayData?.totalTickets || 0}</p>
            </div>
            <div className="bg-success-50 dark:bg-success-500/10 p-4 rounded-lg">
              <p className="text-sm text-text-secondary dark:text-dark-text-secondary">Açık Görevler</p>
              <p className="text-2xl font-bold text-success-600 dark:text-success-500">{displayData?.openTickets || 0}</p>
            </div>
            <div className="bg-highlight-50 dark:bg-highlight-500/10 p-4 rounded-lg">
              <p className="text-sm text-text-secondary dark:text-dark-text-secondary">Tamamlanan</p>
              <p className="text-2xl font-bold text-highlight-600 dark:text-highlight-500">{displayData?.completedTickets || 0}</p>
            </div>
            <div className="bg-warning-50 dark:bg-warning-500/10 p-4 rounded-lg">
              <p className="text-sm text-text-secondary dark:text-dark-text-secondary">Bugün Tamamlanan</p>
              <p className="text-2xl font-bold text-warning-600 dark:text-warning-500">{displayData?.todayCompleted || 0}</p>
            </div>
          </div>

          {/* Task Lists */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <TaskList
              title={`Açık Görevlerim (Tüm Projeler)${dateFilter !== 'all' ? ` - ${getDateFilterLabel(dateFilter)}` : ''}`}
              tasks={displayData?.openTasksList || []}
            />
            <TaskList
              title={`Son Tamamlanan Görevler (Tüm Projeler)${dateFilter !== 'all' ? ` - ${getDateFilterLabel(dateFilter)}` : ''}`}
              tasks={displayData?.completedTasksList || []}
            />
          </div>
        </div>
      )}

      {/* Loading State */}
      {isExpanded && loading && (
        <div className="border-t border-background-secondary dark:border-dark-border p-6">
          <div className="flex items-center justify-center h-32">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-highlight-600 mx-auto mb-4"></div>
              <p className="text-text-secondary dark:text-dark-text-secondary">Görevler yükleniyor...</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllProjectsTasks;

